import { useCallback, type Dispatch, type SetStateAction } from 'react';
import { Option } from '@/components/inputs/AutocompleteInput';
import { useNotification } from '@/hooks';
import { NotificationSeverity } from '@/components/notifications/Notification';
import type { LogisticOperatorsFilter } from '../models';
import {
  buildLogisticOperatorsSearchFilter,
  hasBothSearchSidesActive,
  hasConflictingOperatorIds,
  isLogisticOperatorAutocompleteData,
} from '../utils/logisticOperatorsSearchBar';

interface UseLogisticOperatorsSearchBarHandlersParams {
  localFilter: LogisticOperatorsFilter;
  nameInput: string;
  businessInput: string;
  search: (next: LogisticOperatorsFilter) => void;
  setLocalFilter: Dispatch<SetStateAction<LogisticOperatorsFilter>>;
  setNameInputState: Dispatch<SetStateAction<string>>;
  setBusinessInputState: Dispatch<SetStateAction<string>>;
}

const useLogisticOperatorsSearchBarHandlers = ({
  localFilter,
  nameInput,
  businessInput,
  search,
  setLocalFilter,
  setNameInputState,
  setBusinessInputState,
}: UseLogisticOperatorsSearchBarHandlersParams) => {
  const { showNotification } = useNotification();

  const handleSearch = useCallback(() => {
    if (hasBothSearchSidesActive(localFilter, nameInput, businessInput)) {
      showNotification(
        'Usá un solo criterio: nombre del operador o razón social, no ambos a la vez.',
        NotificationSeverity.warning
      );
      return;
    }

    if (hasConflictingOperatorIds(localFilter)) {
      showNotification(
        'Los operadores elegidos por nombre y por razón social no coinciden. Usá solo un filtro.',
        NotificationSeverity.error
      );
      return;
    }

    search(
      buildLogisticOperatorsSearchFilter(
        localFilter,
        nameInput,
        businessInput
      )
    );
  }, [
    businessInput,
    localFilter,
    nameInput,
    search,
    showNotification,
  ]);

  const handleNameOperatorSelect = useCallback(
    (option: Option | null) => {
      if (!option) {
        setLocalFilter((previous) => ({
          ...previous,
          selectedOperatorIdFromName: undefined,
        }));
        setNameInputState('');
        return;
      }
      if (!isLogisticOperatorAutocompleteData(option.data)) {
        setLocalFilter((previous) => ({
          ...previous,
          selectedOperatorIdFromName: undefined,
        }));
        return;
      }
      const selectedId = option.data.id.trim();
      if (!selectedId) {
        setLocalFilter((previous) => ({
          ...previous,
          selectedOperatorIdFromName: undefined,
        }));
        return;
      }
      setLocalFilter((previous) => ({
        ...previous,
        selectedOperatorIdFromBusiness: undefined,
        selectedOperatorIdFromName: selectedId,
      }));
      setBusinessInputState('');
      setNameInputState('');
    },
    [setBusinessInputState, setLocalFilter, setNameInputState]
  );

  const handleBusinessOperatorSelect = useCallback(
    (option: Option | null) => {
      if (!option) {
        setLocalFilter((previous) => ({
          ...previous,
          selectedOperatorIdFromBusiness: undefined,
        }));
        setBusinessInputState('');
        return;
      }
      if (!isLogisticOperatorAutocompleteData(option.data)) {
        setLocalFilter((previous) => ({
          ...previous,
          selectedOperatorIdFromBusiness: undefined,
        }));
        return;
      }
      const selectedId = option.data.id.trim();
      if (!selectedId) {
        setLocalFilter((previous) => ({
          ...previous,
          selectedOperatorIdFromBusiness: undefined,
        }));
        return;
      }
      setLocalFilter((previous) => ({
        ...previous,
        selectedOperatorIdFromName: undefined,
        selectedOperatorIdFromBusiness: selectedId,
      }));
      setNameInputState('');
      setBusinessInputState('');
    },
    [setBusinessInputState, setLocalFilter, setNameInputState]
  );

  return {
    handleSearch,
    handleNameOperatorSelect,
    handleBusinessOperatorSelect,
  };
};

export default useLogisticOperatorsSearchBarHandlers;
